import { Close } from "@mui/icons-material";
import {
  styled,
  Box,
  debounce,
  Stack,
  TextField,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useParams } from "react-router-dom";

import { GetProductsByCategoriesParams } from "../../api/products/products";
import { incrementRecount } from "../../reducers/products";
import {
  LooseProduct,
  CartPayload,
  LooseItem,
  LooseProductsResponse,
} from "../../types";

import { useAppDispatch } from "../../hooks";
import { getStorageValue } from "../../hooks/useLocalStorage";

import Loading from "../Loading";
import SelectItem from "./SelectItem";
import MagnifyingGlass from "../../assets/icons/magnifying_glass.svg";
import AccessoryPlaceholder from "../../assets/images/accessory.png";

const ProductContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "32px",

  padding: "24px 32px",

  width: "100%",

  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.palette.grey[100],

  backgroundColor: theme.palette.common.white,

  [theme.breakpoints.down("md")]: {
    gap: "16px",
    padding: "16px",
  },
}));

const ScrollContainer = styled(Box)(({ theme }) => ({
  maxHeight: "calc(100vh - 416px)",
  overflowY: "auto",

  [theme.breakpoints.down("md")]: {
    maxHeight: "calc(100vh - 310px)",
  },
}));

interface SelectLooseProductsProps {
  get: (
    params: GetProductsByCategoriesParams,
  ) => Promise<LooseProductsResponse>;
  isPreconfigured?: boolean;
  source?: "configuration" | "selection";
}

const SelectLooseProducts: React.FC<SelectLooseProductsProps> = ({ get, isPreconfigured, source = "selection" }) => {
  const { productId } = useParams();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<LooseProduct[] | null>(null);

  const [cart, setCart] = React.useState<CartPayload | null>(null);

  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");

  const limit = 10;

  const dispatch = useAppDispatch();

  const trackLooseAddToCartEvent = (selectedProduct: LooseProduct) => {
    const hasAnalyticsConsent =
      getStorageValue("cookiesAnalytics", "false") === "true";

    if (!hasAnalyticsConsent) {
      console.info(
        "Analytics event not catalogued: analytics consent is disabled, so this Add to Cart event was not sent.",
      );
      return;
    }

    const gtag = (window as any).gtag;

    if (typeof gtag !== "function") {
      console.info(
        "Analytics event not catalogued: gtag is not available, so this Add to Cart event was not sent.",
      );
      return;
    }

    const preconfiguredModelId = (selectedProduct as LooseProduct & {
      configurationProductId?: string;
    }).configurationProductId;

    const payload: Record<string, string> = {
      productId: selectedProduct.id,
    };

    if (isPreconfigured && preconfiguredModelId) {
      payload.modelId = preconfiguredModelId;
    }

    gtag("event", "add_to_cart", payload);
  };

  const currentProducts = (
    prods: LooseProduct[],
    result: LooseProductsResponse,
  ) =>
    prods?.filter(
      (prod) => !result.products.some((prd) => prd.id === prod.id),
    ) ?? [];

  const handleGetPreconfigured = async (
    params: GetProductsByCategoriesParams,
  ) => {
    if (params.categoryId) {
      const result = await get(params);
      const newOffset = params.offset + result.products.length;
      setOffset(newOffset);

      const isThereMoreItems = newOffset < result.total;
      setHasMore(isThereMoreItems);

      setProducts((prev) => [
        ...currentProducts(prev ?? [], result),
        ...result.products,
      ]);
      setLoading(false);
    }
  };

  const getFilter = React.useCallback(
    (params: GetProductsByCategoriesParams) => {
      handleGetPreconfigured(params);
    },
    [productId, offset],
  );

  const filterDebounce = React.useMemo(
    () => debounce(getFilter, 750),
    [getFilter],
  );

  const handleSearchChange = (searchTerm: string) => {
    setSearch(searchTerm);

    setLoading(true);

    setOffset(0);
    setProducts([]);

    const params: GetProductsByCategoriesParams = {
      categoryId: productId!,
      offset: 0,
      limit,
      search: searchTerm,
    };

    filterDebounce(params);
  };

  const handleUpdateQuantity = (id: string, quantity: number) => {
    // Read current cart from localStorage to avoid stale state issues
    const savedData = localStorage.getItem("Cart");
    const currentCart: CartPayload | null = savedData
      ? JSON.parse(savedData)
      : null;

    const newLooseItems =
      currentCart?.looseItems?.map((item) => {
        if (item.id === id) {
          item.count = quantity;

          return { ...item };
        }
        return item;
      }) ?? ([] as LooseItem[]);

    const updatedCart: CartPayload = {
      items: currentCart?.items ?? [],
      looseItems: newLooseItems,
    };

    handleUpdateCart(updatedCart);
  };

  const handleToggleCart = (id: string) => {
    const matchingAccessory = products?.find(
      (accessory) => accessory.id === id,
    );
    if (matchingAccessory) {
      // Read current cart from localStorage to avoid stale state issues
      const savedData = localStorage.getItem("Cart");
      const currentCart: CartPayload | null = savedData
        ? JSON.parse(savedData)
        : null;

      const item = currentCart?.looseItems?.find((item) => item.id === id);
      if (item) {
        const newLooseItems = currentCart?.looseItems.filter((item) => item.id !== id);
        const updatedCart: CartPayload = {
          items: currentCart?.items ?? [],
          looseItems: newLooseItems ?? [],
        };
        handleUpdateCart(updatedCart);
        dispatch(incrementRecount());

        return;
      }

      const newCartItem: LooseItem = {
        id: matchingAccessory?.id,
        categoryId: productId!,
        name: matchingAccessory?.name,
        imageUrl: matchingAccessory?.imageUrl,
        count: 1,
        type: isPreconfigured ? "preconfigured" : "accessory",
        source,
      };

      const newLooseItems = [...(currentCart?.looseItems ?? []), newCartItem];

      const updatedCart: CartPayload = {
        items: currentCart?.items ?? [],
        looseItems: newLooseItems,
      };

      handleUpdateCart(updatedCart);
      dispatch(incrementRecount());
      trackLooseAddToCartEvent(matchingAccessory);
    }
  };

  const handleUpdateCart = (updatedCart: CartPayload) => {
    localStorage.setItem("Cart", JSON.stringify(updatedCart));
    setCart(updatedCart);
    handleGetCartItems();
  };

  const handleGetCartItems = () => {
    const savedData = localStorage.getItem("Cart");

    if (savedData) {
      const parsedData: CartPayload = JSON.parse(savedData);
      setCart(parsedData);
    }
  };

  const handleClearSearch = () => {
    handleSearchChange("");
  };

  useEffect(() => {
    setLoading(true);
    handleGetCartItems();
  }, []);

  useEffect(() => {
    if (productId) {
      setLoading(true);
      handleGetPreconfigured({ categoryId: productId, offset, limit, search });
    }
  }, [productId]);

  return (
    <ProductContainer>
      <Stack direction="row" spacing={2}>
        <TextField
          fullWidth
          placeholder="Search..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: <img alt="search icon" src={MagnifyingGlass} />,
            endAdornment:
              search === "" ? null : (
                <IconButton onClick={handleClearSearch}>
                  <Close fontSize="small" />
                </IconButton>
              ),
          }}
        />
      </Stack>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            left: "calc(50% - 16px)",
            top: "calc(50% - 16px)",
          }}
        >
          <Loading />
        </Box>
      )}
      <ScrollContainer id="loose-products-scroll">
        <InfiniteScroll
          dataLength={products?.length ?? 0}
          next={() => {
            const params: GetProductsByCategoriesParams = {
              categoryId: productId!,
              offset,
              limit,
              search,
            };
            filterDebounce(params);
          }}
          hasMore={hasMore}
          scrollableTarget="loose-products-scroll"
          loader={<Loading />}
          endMessage={<div></div>}
        >
          {products?.map((accessory, index) => {
            const cartReflection = cart?.looseItems?.find(
              (item) => item.id === accessory.id,
            );
            
            return (
              <SelectItem
                isAdded={cartReflection !== undefined}
                id={accessory.id}
                imageUrl={isPreconfigured ? accessory.imageUrl : AccessoryPlaceholder}
                name={accessory.name}
                quantity={cartReflection?.count ?? 1}
                handleUpdateQuantity={handleUpdateQuantity}
                handleToggleCart={handleToggleCart}
                key={accessory.id + index}
              />
            );
          })}

          {!loading && products?.length === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "200px",
              }}
            >
              <Typography variant="h6" color="grey[600]">
                No items in category
              </Typography>
            </Box>
          )}
        </InfiniteScroll>
      </ScrollContainer>
    </ProductContainer>
  );
};

export default SelectLooseProducts;
